/**
 * Regras da API oficial do WhatsApp que valem igual no servidor e na tela
 * (ADR 0033): a janela de 24 horas, a leitura dos modelos da Meta, os nomes
 * em português do que a Meta devolve em inglês, e a estimativa de custo.
 */

// ─────────────────────────── janela de atendimento ───────────────────────────

/**
 * Depois da última mensagem do cliente, a empresa tem 24 horas para mandar
 * o que quiser. Passado isso, só modelo aprovado pela Meta, até o cliente
 * escrever de novo.
 */
export const HORAS_DA_JANELA = 24
const JANELA_MS = HORAS_DA_JANELA * 60 * 60 * 1000

/** Quando a janela fecha. Sem mensagem do cliente, ela nunca abriu. */
export function fimDaJanela(ultimaDoCliente: Date | string | null | undefined): Date | null {
  if (!ultimaDoCliente) return null
  return new Date(new Date(ultimaDoCliente).getTime() + JANELA_MS)
}

export function janelaAberta(
  ultimaDoCliente: Date | string | null | undefined,
  agora: Date = new Date(),
): boolean {
  const fim = fimDaJanela(ultimaDoCliente)
  return fim !== null && fim.getTime() > agora.getTime()
}

/** "fecha em 3 h", "fecha em 25 min": para o aviso discreto sobre o campo. */
export function tempoAteFechar(fim: Date | string, agora: Date = new Date()): string {
  const minutos = Math.max(0, Math.round((new Date(fim).getTime() - agora.getTime()) / 60000))
  if (minutos < 60) return `${minutos} min`
  const horas = Math.floor(minutos / 60)
  return `${horas} h`
}

// ─────────────────────────── modelos ───────────────────────────

export interface VariavelDoModelo {
  onde: 'cabecalho' | 'corpo'
  /** "1", "2" no modelo posicional; "nome" no modelo com nomes. */
  nome: string
  /** O exemplo que a Meta aprovou junto, quando veio. */
  exemplo: string | null
}

export interface ModeloAnalisado {
  cabecalho: string | null
  corpo: string
  rodape: string | null
  botoes: string[]
  variaveis: VariavelDoModelo[]
  /** O CRM consegue mandar este modelo como está. */
  enviavel: boolean
  /** Por que não, quando não. */
  motivo: string | null
}

const VARIAVEL = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g
// Sem `g`: `test` com `g` guarda posição entre chamadas e erra a segunda.
const TEM_VARIAVEL = /\{\{\s*[A-Za-z0-9_]+\s*\}\}/

function nomesNoTexto(texto: string): string[] {
  const vistos: string[] = []
  for (const achado of texto.matchAll(VARIAVEL)) {
    const nome = achado[1] ?? ''
    if (nome && !vistos.includes(nome)) vistos.push(nome)
  }
  return vistos
}

function ehObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor)
}

function exemplosDoCorpo(componente: Record<string, unknown>): string[] {
  const exemplo = ehObjeto(componente.example) ? componente.example : null
  const posicional = exemplo?.body_text
  if (Array.isArray(posicional) && Array.isArray(posicional[0])) {
    return (posicional[0] as unknown[]).map((v) => String(v))
  }
  const comNome = exemplo?.body_text_named_params
  if (Array.isArray(comNome)) {
    return comNome.map((p) => (ehObjeto(p) ? String(p.example ?? '') : ''))
  }
  return []
}

/**
 * Lê os componentes de um modelo como a Meta devolve: cabeçalho, corpo,
 * rodapé e botões, e as variáveis `{{1}}` ou `{{nome}}` que precisam de valor.
 *
 * Fica de fora, por ora, o que pede mais do que texto: cabeçalho com foto,
 * vídeo ou documento, e botão de link com parte variável. Esses aparecem na
 * lista como "não enviável pelo CRM", com o motivo, em vez de falhar no envio.
 */
export function analisarModelo(componentes: unknown): ModeloAnalisado {
  const lista = Array.isArray(componentes) ? componentes.filter(ehObjeto) : []
  let cabecalho: string | null = null
  let corpo = ''
  let rodape: string | null = null
  const botoes: string[] = []
  const variaveis: VariavelDoModelo[] = []
  let motivo: string | null = null

  for (const componente of lista) {
    const tipo = String(componente.type ?? '').toUpperCase()
    if (tipo === 'HEADER') {
      const formato = String(componente.format ?? 'TEXT').toUpperCase()
      if (formato !== 'TEXT') {
        motivo ??= 'O cabeçalho tem foto, vídeo ou documento, que o CRM ainda não manda.'
        continue
      }
      cabecalho = String(componente.text ?? '') || null
      if (cabecalho) {
        const exemplo = ehObjeto(componente.example) ? componente.example.header_text : null
        const exemplos = Array.isArray(exemplo) ? exemplo.map((v) => String(v)) : []
        nomesNoTexto(cabecalho).forEach((nome, i) =>
          variaveis.push({ onde: 'cabecalho', nome, exemplo: exemplos[i] ?? null }),
        )
      }
    } else if (tipo === 'BODY') {
      corpo = String(componente.text ?? '')
      const exemplos = exemplosDoCorpo(componente)
      nomesNoTexto(corpo).forEach((nome, i) =>
        variaveis.push({ onde: 'corpo', nome, exemplo: exemplos[i] ?? null }),
      )
    } else if (tipo === 'FOOTER') {
      rodape = String(componente.text ?? '') || null
    } else if (tipo === 'BUTTONS' && Array.isArray(componente.buttons)) {
      for (const botao of componente.buttons.filter(ehObjeto)) {
        botoes.push(String(botao.text ?? ''))
        if (
          String(botao.type ?? '').toUpperCase() === 'URL' &&
          TEM_VARIAVEL.test(String(botao.url))
        ) {
          motivo ??= 'Um botão de link tem parte variável, que o CRM ainda não preenche.'
        }
      }
    }
  }

  if (!corpo && !motivo) motivo = 'O modelo não tem corpo de texto.'
  return { cabecalho, corpo, rodape, botoes, variaveis, enviavel: motivo === null, motivo }
}

/** A chave de um valor no formulário: a mesma variável pode aparecer em dois lugares. */
export function chaveDaVariavel(v: Pick<VariavelDoModelo, 'onde' | 'nome'>): string {
  return `${v.onde}:${v.nome}`
}

/**
 * O texto que o cliente vai ler, com os valores no lugar das variáveis. É o
 * que fica gravado na conversa, porque o CRM mostra o que o cliente recebeu.
 */
export function preencherModelo(
  modelo: Pick<ModeloAnalisado, 'cabecalho' | 'corpo' | 'rodape'>,
  valores: Record<string, string>,
): string {
  const trocar = (texto: string, onde: VariavelDoModelo['onde']) =>
    texto.replace(VARIAVEL, (inteiro, nome: string) => {
      const valor = valores[chaveDaVariavel({ onde, nome })]
      return valor && valor.trim() ? valor.trim() : inteiro
    })
  return [
    modelo.cabecalho ? `*${trocar(modelo.cabecalho, 'cabecalho')}*` : null,
    trocar(modelo.corpo, 'corpo'),
    modelo.rodape ? `_${modelo.rodape}_` : null,
  ]
    .filter(Boolean)
    .join('\n\n')
}

/** Variável que ficou sem valor. A Meta recusa o envio inteiro por uma. */
export function variaveisSemValor(
  modelo: Pick<ModeloAnalisado, 'variaveis'>,
  valores: Record<string, string>,
): VariavelDoModelo[] {
  return modelo.variaveis.filter((v) => !(valores[chaveDaVariavel(v)] ?? '').trim())
}

// ─────────────────────────── nomes em português ───────────────────────────

export const ROTULO_QUALIDADE: Record<
  string,
  { rotulo: string; tom: 'bom' | 'atencao' | 'ruim' | 'neutro'; explicacao: string }
> = {
  GREEN: {
    rotulo: 'Alta',
    tom: 'bom',
    explicacao: 'Os clientes não estão bloqueando nem denunciando o número.',
  },
  YELLOW: {
    rotulo: 'Média',
    tom: 'atencao',
    explicacao:
      'Alguns clientes bloquearam ou denunciaram nos últimos dias. Vale rever os modelos de marketing.',
  },
  RED: {
    rotulo: 'Baixa',
    tom: 'ruim',
    explicacao:
      'Muitos bloqueios ou denúncias. A Meta pode baixar o limite diário ou suspender o número.',
  },
  UNKNOWN: {
    rotulo: 'Sem nota ainda',
    tom: 'neutro',
    explicacao: 'A Meta só dá nota depois de o número mandar mensagens.',
  },
}

/**
 * Quantos clientes o número pode chamar primeiro por dia (quem escreve
 * antes não conta). Sobe sozinho conforme o uso e a qualidade.
 */
export const ROTULO_LIMITE_DIARIO: Record<string, string> = {
  TIER_50: '50 clientes novos por dia',
  TIER_250: '250 clientes novos por dia',
  TIER_1K: '1.000 clientes novos por dia',
  TIER_2K: '2.000 clientes novos por dia',
  TIER_10K: '10.000 clientes novos por dia',
  TIER_100K: '100.000 clientes novos por dia',
  TIER_UNLIMITED: 'Sem limite de clientes novos por dia',
}

export const ROTULO_CATEGORIA_MODELO: Record<string, string> = {
  MARKETING: 'Marketing',
  UTILITY: 'Utilidade',
  AUTHENTICATION: 'Autenticação',
}

export const ROTULO_SITUACAO_MODELO: Record<
  string,
  { rotulo: string; tom: 'bom' | 'atencao' | 'ruim' }
> = {
  APPROVED: { rotulo: 'Aprovado', tom: 'bom' },
  PENDING: { rotulo: 'Em análise', tom: 'atencao' },
  IN_APPEAL: { rotulo: 'Em recurso', tom: 'atencao' },
  REJECTED: { rotulo: 'Recusado', tom: 'ruim' },
  PAUSED: { rotulo: 'Pausado', tom: 'ruim' },
  DISABLED: { rotulo: 'Desativado', tom: 'ruim' },
}

/** Por que a Meta recusou um modelo, no código que ela devolve. */
export const ROTULO_MOTIVO_RECUSA: Record<string, string> = {
  ABUSIVE_CONTENT: 'Conteúdo que fere as políticas da Meta.',
  INCORRECT_CATEGORY: 'A categoria escolhida não serve para este texto.',
  INVALID_FORMAT: 'Formato inválido: confira as variáveis e o texto.',
  PROMOTIONAL: 'Conteúdo promocional que a Meta não aceitou neste modelo.',
  SCAM: 'A Meta entendeu como possível golpe.',
  TAG_CONTENT_MISMATCH: 'A categoria não combina com o conteúdo.',
}

// ─────────────────────────── custo ───────────────────────────

export interface PrecosPorCategoria {
  marketing: number
  utilidade: number
  autenticacao: number
}

export interface ContagemDeModelos {
  marketing: number
  utilidade: number
  autenticacao: number
}

/**
 * Estimativa do mês pela tabela da Meta: cobra-se por modelo entregue,
 * conforme a categoria. Resposta dentro da janela de 24 horas é grátis, e o
 * modelo de utilidade mandado com a janela aberta também, por isso a contagem
 * que chega aqui já vem sem eles.
 */
export function estimarCusto(
  contagem: ContagemDeModelos,
  precos: PrecosPorCategoria,
): { total: number; porCategoria: ContagemDeModelos } {
  const porCategoria = {
    marketing: contagem.marketing * precos.marketing,
    utilidade: contagem.utilidade * precos.utilidade,
    autenticacao: contagem.autenticacao * precos.autenticacao,
  }
  const total = porCategoria.marketing + porCategoria.utilidade + porCategoria.autenticacao
  return { total: Math.round(total * 100) / 100, porCategoria }
}

/** A categoria da Meta na chave da contagem. */
export function chaveDaCategoria(categoria: string): keyof ContagemDeModelos | null {
  const c = categoria.toUpperCase()
  if (c === 'MARKETING') return 'marketing'
  if (c === 'UTILITY') return 'utilidade'
  if (c === 'AUTHENTICATION') return 'autenticacao'
  return null
}
